import React, { useEffect, useState } from "react";
import Footer from "../Component/Footer";
import Header from "../Component/Header";
import { MapPin, Target, Calendar, Award } from "lucide-react";

function WhereWeDoIt() {
  const [isVisible, setIsVisible] = useState({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({
              ...prev,
              [entry.target.id]: true
            }));
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('[data-animate]');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const locations = [
    {
      name: "Kayunga",
      description: "Our primary hub with comprehensive community development programs",
      color: "from-blue-500 to-cyan-500",
      icon: "🏘️"
    },
    {
      name: "Jinja", 
      description: "Strategic initiatives targeting local community needs",
      color: "from-green-500 to-emerald-500",
      icon: "🌊"
    },
    {
      name: "Mukono",
      description: "Expanding our reach with tailored development programs",
      color: "from-purple-500 to-pink-500", 
      icon: "🏛️"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-green-50 to-blue-50">
      <Header />
      
      {/* Hero Section */}
      <div className="relative overflow-hidden pt-40 pb-20">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-64 h-64 bg-green-200/30 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-48 h-48 bg-blue-200/30 rounded-full blur-2xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/4 w-32 h-32 bg-purple-200/30 rounded-full blur-xl animate-pulse delay-2000"></div>
          
          {/* Decorative map elements */}
          <div className="absolute top-1/3 right-1/4 w-96 h-96 opacity-5">
            <svg viewBox="0 0 100 100" className="w-full h-full text-green-600 animate-pulse">
              <circle cx="30" cy="30" r="8" fill="currentColor"/>
              <circle cx="70" cy="45" r="8" fill="currentColor"/>
              <circle cx="50" cy="70" r="8" fill="currentColor"/>
              <path d="M30,30 L70,45 L50,70" stroke="currentColor" strokeWidth="2" fill="none"/>
            </svg>
          </div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4">
          <div 
            id="hero-content"
            data-animate
            className={`text-center transform transition-all duration-1000 ${
              isVisible['hero-content'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <div className="inline-flex items-center bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <MapPin className="w-4 h-4 mr-2" />
              Geographic Coverage
            </div>
            
            <h1 className="text-4xl md:text-6xl font-black mb-8">
              <span className="bg-gradient-to-r from-green-600 via-blue-600 to-purple-600 bg-clip-text text-transparent">
                Where We Work!
              </span>
            </h1>
            
            <div className="max-w-3xl mx-auto">
              <div className="bg-white/80 backdrop-blur-xl rounded-3xl p-8 md:p-12 shadow-xl border border-green-100">
                <div className="flex items-center justify-center mb-6">
                  <Calendar className="w-8 h-8 text-green-600 mr-3" />
                  <span className="text-2xl font-bold text-green-700">Since 2021</span>
                </div>
                <p className="text-xl md:text-2xl text-gray-700 leading-relaxed">
                  We have <strong className="text-green-700">initiatives and community development programs</strong> 
                  operating within the strategic areas of <strong className="text-blue-700">Kayunga</strong>, 
                  <strong className="text-green-700"> Jinja</strong>, and <strong className="text-purple-700">Mukono</strong>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Locations Section */}
      <div className="py-20 bg-white/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4">
          <div 
            id="locations-header"
            data-animate
            className={`text-center mb-16 transform transition-all duration-1000 ${
              isVisible['locations-header'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
              Our <span className="bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">Locations</span>
            </h2>
            <div className="w-32 h-1 bg-gradient-to-r from-green-500 to-blue-500 rounded-full mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {locations.map((location, index) => (
              <div
                key={index}
                id={`location-${index}`}
                data-animate
                className={`transform transition-all duration-1000 delay-${index * 300} ${
                  isVisible[`location-${index}`] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                }`}
              >
                <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 h-full">
                  <div className={`w-20 h-20 bg-gradient-to-r ${location.color} rounded-3xl flex items-center justify-center mb-6 mx-auto`}>
                    <span className="text-3xl">{location.icon}</span>
                  </div>
                  
                  <h3 className="text-2xl font-bold text-gray-800 mb-4 text-center">{location.name}</h3>
                  <p className="text-gray-600 text-center leading-relaxed">{location.description}</p>
                  
                  <div className="mt-6 text-center">
                    <div className={`inline-flex items-center bg-gradient-to-r ${location.color} bg-opacity-10 px-4 py-2 rounded-full`}>
                      <MapPin className="w-4 h-4 mr-2 text-gray-600" />
                      <span className="font-semibold text-gray-800">Active Since 2021</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Coverage Map Visual */}
      <div className="py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div 
            id="coverage-visual"
            data-animate
            className={`transform transition-all duration-1000 ${
              isVisible['coverage-visual'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <div className="bg-gradient-to-r from-green-600 to-blue-600 rounded-3xl p-1 shadow-2xl">
              <div className="bg-white rounded-3xl p-8 md:p-12">
                <Award className="w-16 h-16 text-green-600 mx-auto mb-6" />
                <h3 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
                  Strategic <span className="bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">Coverage</span>
                </h3>
                <p className="text-xl text-gray-700 leading-relaxed mb-8">
                  Our three-district approach ensures comprehensive regional impact 
                  while maintaining deep community connections and sustainable development programs.
                </p>
                
                <div className="flex justify-center items-center space-x-8">
                  <div className="text-center">
                    <div className="text-3xl font-black text-green-600">3</div>
                    <div className="text-sm text-gray-600">Districts</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-black text-blue-600">2021</div>
                    <div className="text-sm text-gray-600">Since</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-black text-purple-600">∞</div>
                    <div className="text-sm text-gray-600">Communities</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default WhereWeDoIt;